// Proveedor global de tooltips temáticos. Intercepta cualquier `title` (enlaces,
// botones, iconos, campos) y `alt` (imágenes) de la app y lo renderiza como una
// burbuja estilizada que hereda el tema/skin activo (claro/oscuro) y reacciona al
// modo neón. Suprime el tooltip nativo del navegador mientras se muestra y lo
// restaura al salir. No afecta a los elementos ya envueltos por el componente
// <Tooltip> (clase .tooltip-buttom), que conservan su propio tooltip.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const GAP = 8;

export default function GlobalTooltipProvider() {
  const tipRef = useRef(null);
  const st = useRef({ el: null, from: null, savedTitle: null });

  useEffect(() => {
    const tip = document.createElement("div");
    tip.className = "bhgc-tip";
    tip.setAttribute("role", "tooltip");
    tip.setAttribute("aria-hidden", "true");
    document.body.appendChild(tip);
    tipRef.current = tip;

    const resolve = (el) => {
      // Ya tiene su propio tooltip (componente <Tooltip>) → no duplicar.
      if (el.closest && el.closest(".tooltip-buttom, .bhgc-tip")) return null;
      const t = el.getAttribute && el.getAttribute("title");
      if (t && t.trim()) return { text: t.trim(), from: "title" };
      const tag = el.tagName;
      if (tag === "IMG" || tag === "AREA") {
        const a = el.getAttribute && el.getAttribute("alt");
        if (a && a.trim()) return { text: a.trim(), from: "alt" };
      }
      return null;
    };

    const position = (el, text) => {
      const node = tipRef.current;
      if (!node) return;
      node.textContent = text;
      node.style.left = "0px";
      node.style.top = "0px";
      node.setAttribute("data-show", "true");
      node.setAttribute("aria-hidden", "false");
      const tw = node.offsetWidth || 160;
      const th = node.offsetHeight || 28;
      const r = el.getBoundingClientRect();
      let top = r.top - th - GAP;
      let left = r.left + r.width / 2 - tw / 2;
      if (top < GAP) top = r.bottom + GAP; // sin espacio arriba → abajo
      if (left < GAP) left = GAP;
      if (left + tw > window.innerWidth - GAP) left = window.innerWidth - tw - GAP;
      node.style.left = `${Math.round(left)}px`;
      node.style.top = `${Math.round(top)}px`;
    };

    const show = (el) => {
      if (!el || !el.isConnected) return;
      const info = resolve(el);
      if (!info) return;
      if (st.current.el === el) return;
      // Suprime el tooltip nativo solo para titles (alt no genera tooltip nativo).
      if (info.from === "title") {
        st.current.savedTitle = el.getAttribute("title");
        el.setAttribute("data-orig-title", st.current.savedTitle);
        el.removeAttribute("title");
      }
      st.current.el = el;
      st.current.from = info.from;
      position(el, info.text);
    };

    const hide = () => {
      const el = st.current.el;
      if (el && st.current.from === "title" && st.current.savedTitle != null && el.isConnected) {
        el.setAttribute("title", st.current.savedTitle);
      }
      st.current.el = null;
      st.current.from = null;
      st.current.savedTitle = null;
      const node = tipRef.current;
      if (node) {
        node.removeAttribute("data-show");
        node.setAttribute("aria-hidden", "true");
      }
    };

    const onOver = (e) => {
      if (e.pointerType === "touch") return; // móvil: no mostrar (evita molestia)
      const target = e.target;
      if (!target || !target.closest) return;
      const el = target.closest('[title]:not([title=""]), img[alt]:not([alt=""]), area[alt]:not([alt=""])');
      if (el) show(el);
    };

    const onOut = (e) => {
      const el = st.current.el;
      if (!el) return;
      const related = e.relatedTarget;
      if (related && el.contains(related)) return;
      hide();
    };

    const onFocusIn = (e) => {
      const target = e.target;
      if (!target || !target.closest) return;
      const el = target.closest('[title]:not([title=""]), img[alt]:not([alt=""]), area[alt]:not([alt=""])');
      if (el) show(el);
    };

    const onFocusOut = (e) => {
      const el = st.current.el;
      if (!el) return;
      const related = e.relatedTarget;
      if (related && el.contains(related)) return;
      hide();
    };

    const onScroll = () => {
      const el = st.current.el;
      if (el && el.isConnected) position(el, tipRef.current?.textContent || "");
      else hide();
    };

    const onHideNow = () => hide();

    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("pointerout", onOut, true);
    document.addEventListener("pointerdown", onHideNow, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("keydown", onHideNow, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("pointerout", onOut, true);
      document.removeEventListener("pointerdown", onHideNow, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("keydown", onHideNow, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      tip.remove();
    };
  }, []);

  return createPortal(null, document.body);
}